import { c as _c } from "react/compiler-runtime";
import { type BlocoDocumento, type ContextoPaginaDocumento, DocumentoPaginado, MarcadorSecao, type SecaoDocumento, SumarioDocumento } from '@/shared/components/documentos/paginado';
import type { SentencaArbitralDados } from '../../../types/sentencaArbitral.types';
import { FaixaAutenticidadeSentenca } from './FaixaAutenticidadeSentenca';
import { SecaoCapaSentenca } from './SecaoCapaSentenca';
import { PainelResumoDecisao, SecaoDecisaoIntro } from './SecaoDecisaoSentenca';
import { SecaoEncerramentoSentenca } from './SecaoEncerramentoSentenca';
import { SecaoGlossarioIntro, TabelaGlossarioSentenca } from './SecaoGlossarioSentenca';
import { SecaoIdentificacaoIntro, TabelaIdentificacaoSentenca } from './SecaoIdentificacaoSentenca';
import { SecaoLinhaTempoSentenca } from './SecaoLinhaTempoSentenca';
import { SecaoPartesIntro, TabelaGrupoPartesSentenca } from './SecaoPartesSentenca';
import { AvisoDuvidaSentenca, PainelValidadeSentenca } from './SecaoValidadeSentenca';
import { NUMERO_SECAO_LINHA_TEMPO, secoesVisiveisSentenca, sumarioDescricaoSentenca, TEXTOS_SENTENCA_ARBITRAL } from './secoesSentencaArbitral';
import { AberturaTextoOficial, BlocoTextoOficialSentenca, FechamentoTextoOficial, TituloCapituloSentenca } from './TextoOficialSentenca';
function marcador(secoes: readonly SecaoDocumento[], numero: number, novaPagina = false): BlocoDocumento {
  return {
    id: `marcador-${numero}`,
    manterComProximo: true,
    novaPagina,
    node: <MarcadorSecao numero={numero} secoes={secoes} />
  };
}
function blocosCapa(sentenca: SentencaArbitralDados, secoes: readonly SecaoDocumento[]): BlocoDocumento[] {
  return [{
    id: 'capa',
    manterComProximo: true,
    node: <SecaoCapaSentenca sentenca={sentenca} />
  }, {
    id: 'sumario',
    manterComProximo: true,
    node: <SumarioDocumento descricao={sumarioDescricaoSentenca(sentenca.linhaTempo.length > 0)} secoes={secoes} titulo={TEXTOS_SENTENCA_ARBITRAL.sumarioTitulo} />
  }, {
    id: 'faixa-autenticidade',
    node: <FaixaAutenticidadeSentenca codigoAutenticacao={sentenca.codigoAutenticacao} identificacao={sentenca.identificacao} />
  }];
}
function blocosIdentificacao(sentenca: SentencaArbitralDados, secoes: readonly SecaoDocumento[]): BlocoDocumento[] {
  return [marcador(secoes, 1), {
    id: 'identificacao-intro',
    manterComProximo: true,
    node: <SecaoIdentificacaoIntro />
  }, {
    id: 'identificacao-tabela',
    node: <TabelaIdentificacaoSentenca identificacao={sentenca.identificacao} />
  }];
}
function blocosPartes(sentenca: SentencaArbitralDados, secoes: readonly SecaoDocumento[]): BlocoDocumento[] {
  return [marcador(secoes, 2), {
    id: 'partes-intro',
    manterComProximo: true,
    node: <SecaoPartesIntro />
  }, ...sentenca.gruposPartes.map(grupo => ({
    id: `partes-${grupo.id}`,
    node: <TabelaGrupoPartesSentenca grupo={grupo} />
  }))];
}
function blocosTextoOficial(sentenca: SentencaArbitralDados, secoes: readonly SecaoDocumento[]): BlocoDocumento[] {
  const blocosCapitulos = sentenca.capitulos.flatMap<BlocoDocumento>(capitulo => [...(capitulo.titulo ? [{
    id: `capitulo-${capitulo.id}`,
    manterComProximo: true,
    render: (contexto: ContextoPaginaDocumento) => <TituloCapituloSentenca mostrarContinuacao={contexto.primeiroDaPagina} titulo={capitulo.titulo ?? ''} formato={capitulo.formatoTitulo} />
  }] : []), ...capitulo.blocos.map(bloco => ({
    id: `${capitulo.id}-${bloco.id}`,
    render: (contexto: ContextoPaginaDocumento) => <BlocoTextoOficialSentenca bloco={bloco} mostrarContinuacao={contexto.primeiroDaPagina} />
  }))]);
  // rn14: o último bloco de capítulo forma grupo indivisível com o fechamento — a assinatura do
  // árbitro nunca cai sozinha em página nova (era o defeito apontado no vídeo do handoff).
  const ultimoBlocoDeCapitulo = blocosCapitulos[blocosCapitulos.length - 1];
  const blocosCapitulosComFechamentoJunto = ultimoBlocoDeCapitulo ? [...blocosCapitulos.slice(0, -1), {
    ...ultimoBlocoDeCapitulo,
    manterComProximo: true
  }] : blocosCapitulos;

  // rn8/rn9: com zero blocos de resumo a seção INTEIRA some — nem a frase de intro ("Primeiro,
  // em três blocos…") nem o selo sobrevivem a uma seção vazia.
  const blocosResumo: BlocoDocumento[] = sentenca.resumoDecisao.length > 0 ? [{
    id: 'decisao-intro',
    manterComProximo: true,
    node: <SecaoDecisaoIntro />
  }, {
    id: 'decisao-resumo',
    node: <PainelResumoDecisao blocos={sentenca.resumoDecisao} />
  }] : [];
  return [marcador(secoes, 3), ...blocosResumo, {
    id: 'texto-oficial-abertura',
    manterComProximo: true,
    node: <AberturaTextoOficial qualificacoes={sentenca.qualificacoes} requerimentoNumero={sentenca.identificacao.requerimentoNumero} titulo={sentenca.tituloTextoOficial} />
  }, ...blocosCapitulosComFechamentoJunto, {
    id: 'texto-oficial-assinatura',
    render: contexto => <FechamentoTextoOficial assinatura={sentenca.assinatura} mostrarContinuacao={contexto.primeiroDaPagina} />
  }, {
    id: 'painel-validade',
    manterComProximo: true,
    node: <PainelValidadeSentenca codigoAutenticacao={sentenca.codigoAutenticacao} identificacao={sentenca.identificacao} />
  }, {
    id: 'aviso-duvida',
    node: <AvisoDuvidaSentenca orientacao={sentenca.orientacaoDuvida} />
  }];
}
function blocosLeituraApoiada(sentenca: SentencaArbitralDados, secoes: readonly SecaoDocumento[]): BlocoDocumento[] {
  const temLinhaTempo = sentenca.linhaTempo.length > 0;
  // E4.2: processo sem marco nenhum não exibe a seção "Como o processo caminhou" — e as seções
  // visíveis são RENUMERADAS (sumário, marcadores e "Seção N de M" ficam coerentes entre si).
  const blocosLinhaTempo: BlocoDocumento[] = temLinhaTempo ? [marcador(secoes, NUMERO_SECAO_LINHA_TEMPO, true), {
    id: 'linha-tempo',
    node: <SecaoLinhaTempoSentenca eventos={sentenca.linhaTempo} />
  }] : [];
  const numeroGlossario = secoes[secoes.length - 1].numero;
  return [...blocosLinhaTempo, marcador(secoes, numeroGlossario), {
    id: 'glossario-intro',
    manterComProximo: true,
    node: <SecaoGlossarioIntro />
  }, {
    id: 'glossario-tabela',
    node: <TabelaGlossarioSentenca termos={sentenca.glossario} />
  }, {
    id: 'encerramento',
    node: <SecaoEncerramentoSentenca codigoAutenticacao={sentenca.codigoAutenticacao} instituicao={sentenca.instituicao} />
  }];
}
export function montarBlocosSentenca(sentenca: SentencaArbitralDados): BlocoDocumento[] {
  const secoes = secoesVisiveisSentenca(sentenca.linhaTempo.length > 0);
  return [...blocosCapa(sentenca, secoes), ...blocosIdentificacao(sentenca, secoes), ...blocosPartes(sentenca, secoes), ...blocosTextoOficial(sentenca, secoes), ...blocosLeituraApoiada(sentenca, secoes)];
}
interface SentencaArbitralDocumentoProps {
  sentenca: SentencaArbitralDados;
}
export function SentencaArbitralDocumento(t0) {
  const $ = _c(6);
  const {
    sentenca
  } = t0;
  let t1;
  if ($[0] !== sentenca) {
    t1 = montarBlocosSentenca(sentenca);
    $[0] = sentenca;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== sentenca.codigoAutenticacao || $[3] !== sentenca.identificacao.autenticidadeUrlExibicao || $[4] !== t1) {
    t2 = <DocumentoPaginado blocos={t1} codigoAutenticacao={sentenca.codigoAutenticacao} urlAutenticacaoExibicao={sentenca.identificacao.autenticidadeUrlExibicao} />;
    $[2] = sentenca.codigoAutenticacao;
    $[3] = sentenca.identificacao.autenticidadeUrlExibicao;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}